import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Custom hook to handle upload blocking functionality
 * Provides protection against page refresh and navigation during uploads
 */
export const useUploadBlocking = (isUploading) => {
  const navigate = useNavigate();
  const [showNavModal, setShowNavModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  // --- REFRESH BLOCKING (NATIVE & CUSTOM) ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = ""; // Show default browser prompt (required to block refresh/close tab)
      }
    };

    const handleKeyDown = (e) => {
      // ESC key handling for modals
      if (e.key === "Escape") {
        if (showRefreshModal) {
          setShowRefreshModal(false);
          return;
        }
        if (showNavModal) {
          setShowNavModal(false);
          return;
        }
      }

      // F5 and Ctrl+R blocking
      if (isUploading && (e.key === "F5" || (e.ctrlKey && e.key === "r"))) {
        e.preventDefault();
        setShowRefreshModal(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isUploading, showRefreshModal, showNavModal]);

  // --- NAVIGATION BLOCKING (INTERNAL LINKS) ---
  useEffect(() => {
    const handleInternalClick = (e) => {
      if (!isUploading) return;

      // Find the closest <a> tag to the click point (to catch both icon/text in links)
      const anchor = e.target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      
      // Only block internal links (not hashtag/javascript links)
      if (href && href.startsWith("/") && !href.startsWith("#")) {
        e.preventDefault();
        e.stopPropagation();
        setPendingPath(href);
        setShowNavModal(true);
      }
    };

    // Listen at document level to catch breadcrumb and menu outside component
    document.addEventListener("click", handleInternalClick, true);
    return () => document.removeEventListener("click", handleInternalClick, true);
  }, [isUploading]);

  // Handler to close navigation modal
  const handleCloseNavModal = () => {
    setShowNavModal(false);
    setPendingPath(null);
  };

  // Handler to close refresh modal
  const handleCloseRefreshModal = () => {
    setShowRefreshModal(false);
  };

  return {
    // Modal states
    showNavModal,
    showRefreshModal,
    
    // Modal handlers
    handleCloseNavModal,
    handleCloseRefreshModal,
    
    // Pending navigation path
    pendingPath,
  };
};